import React, { useEffect, useState } from "react";
import { fetchVentasAdmin, actualizarEstadoVenta } from "../services/api";
import { formatPrice } from "../utils/format";

// ── Estados disponibles ──────────────────────────────────────────────────────
const ESTADOS_ENVIO = ["PENDIENTE", "ENVIADO", "ENTREGADO", "FINALIZADO", "CANCELADO"];

const BADGE_PAGO = {
  PENDIENTE: { bg: "#fef9c3", color: "#854d0e" },
  PAGADO:    { bg: "#dcfce7", color: "#166534" },
  FALLIDO:   { bg: "#fee2e2", color: "#991b1b" },
};

const BADGE_ENVIO = {
  PENDIENTE:  { bg: "#f1f5f9", color: "#475569" },
  ENVIADO:    { bg: "#dbeafe", color: "#1e40af" },
  ENTREGADO:  { bg: "#d1fae5", color: "#065f46" },
  FINALIZADO: { bg: "#f3e8ff", color: "#6b21a8" },
  CANCELADO:  { bg: "#fee2e2", color: "#991b1b" },
};

function Badge({ texto, mapa }) {
  const st = (mapa || BADGE_ENVIO)[texto] || { bg: "#f1f5f9", color: "#334155" };
  return (
    <span style={{
      backgroundColor: st.bg, color: st.color,
      padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {texto || "—"}
    </span>
  );
}

// ── Fila de pedido ────────────────────────────────────────────────────────────
function FilaPedido({ v, onCambiarEstado, onImprimir }) {
  const [estadoSel, setEstadoSel] = useState(v.estadoEnvio || "PENDIENTE");
  const [guardando, setGuardando] = useState(false);
  const [abierto,   setAbierto]   = useState(false);

  const handleCambiar = async () => {
    if (estadoSel === v.estadoEnvio) return;
    if (!window.confirm(`¿Cambiar estado de envío a "${estadoSel}"?`)) return;
    setGuardando(true);
    await onCambiarEstado(v.id, estadoSel);
    setGuardando(false);
  };

  return (
    <>
      <tr style={st.fila}>
        {/* Expandir */}
        <td>
          <button onClick={() => setAbierto(!abierto)} style={st.btnExpand}>
            {abierto ? "▲" : "▼"}
          </button>
        </td>
        <td style={{ fontWeight: 700 }}>#{v.id}</td>
        <td>
          <div style={{ fontWeight: 600 }}>{v.nombreCliente} {v.apellidoCliente}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{v.rutCliente}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{v.email}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{v.telefono}</div>
        </td>
        <td>
          <div>{v.direccion}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{v.comuna}, {v.ciudad}</div>
        </td>
        <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>${formatPrice(v.total)} <small style={{ color: '#888', fontWeight: 'normal' }}>+ IVA</small></td>
        <td><Badge texto={v.estadoPago}  mapa={BADGE_PAGO} /></td>
        <td><Badge texto={v.estadoEnvio} mapa={BADGE_ENVIO} /></td>
        <td style={{ fontSize: 11, whiteSpace: "nowrap", color: "#666" }}>
          {v.fechaVenta ? new Date(v.fechaVenta).toLocaleString("es-CL") : "—"}
        </td>

        {/* ── Acciones ── */}
        <td>
          <div style={st.acciones}>
            {/* Selector de estado de envío */}
            <div style={st.selectorGrupo}>
              <label style={st.labelSelect}>Estado envío:</label>
              <select
                value={estadoSel}
                onChange={e => setEstadoSel(e.target.value)}
                style={st.select}
              >
                {ESTADOS_ENVIO.map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
              <button
                onClick={handleCambiar}
                disabled={guardando || estadoSel === v.estadoEnvio}
                style={{
                  ...st.btnCambiar,
                  opacity: estadoSel === v.estadoEnvio ? 0.4 : 1,
                }}
              >
                {guardando ? "..." : "✔ Aplicar"}
              </button>
            </div>

            {/* Botón etiqueta */}
            <button onClick={() => onImprimir(v)} style={st.btnEtiqueta}>
              🖨️ Etiqueta
            </button>
          </div>
        </td>
      </tr>

      {/* Fila expandible con detalle de productos */}
      {abierto && (
        <tr style={{ backgroundColor: "#f8fafc" }}>
          <td colSpan={9} style={{ padding: "10px 20px" }}>
            <strong style={{ fontSize: 12 }}>Productos del pedido:</strong>
            <table style={st.tablaDetalle}>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cant.</th>
                  <th>Precio unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {v.detalles?.map((d, i) => (
                  <tr key={i}>
                    <td>{d.producto?.nombre || "—"}</td>
                    <td>{d.cantidad}</td>
                    <td>${formatPrice(d.precioUnitario)} + IVA</td>
                    <td>${formatPrice(d.cantidad * d.precioUnitario)} + IVA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
function AdminVentas() {
  const [ventas,  setVentas]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [filtro,  setFiltro]  = useState("TODOS");

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await fetchVentasAdmin();
      setVentas(res.data);
    } catch {
      setError("Error al cargar las ventas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleCambiarEstado = async (id, estadoEnvio) => {
    try {
      await actualizarEstadoVenta(id, estadoEnvio);
      await cargar();
    } catch (e) {
      alert(e.response?.data || "Error al actualizar estado");
    }
  };

  const imprimirEtiqueta = (v) => {
    const w = window.open("", "_blank", "width=420,height=580");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Etiqueta #${v.id}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:0;padding:20px}
        .etiqueta{border:2px solid #000;padding:20px;width:360px}
        .logo{font-size:18px;font-weight:bold;text-align:center;margin-bottom:12px;border-bottom:1px solid #000;padding-bottom:8px}
        .pedido{text-align:center;font-size:22px;font-weight:bold;border:2px solid #000;padding:8px;margin-bottom:12px}
        .sec{margin-bottom:10px}
        .tit{font-size:10px;color:#555;text-transform:uppercase;letter-spacing:1px}
        .val{font-size:14px;font-weight:bold;margin-top:2px}
        @media print{body{padding:0}button{display:none}}
      </style></head><body>
      <div class="etiqueta">
        <div class="logo">✈️ HANGAR SUPPLY</div>
        <div class="pedido">PEDIDO #${v.id}</div>
        <div class="sec"><div class="tit">Destinatario</div>
          <div class="val">${v.nombreCliente||""} ${v.apellidoCliente||""}</div></div>
        <div class="sec"><div class="tit">RUT</div>
          <div class="val">${v.rutCliente||"—"}</div></div>
        <div class="sec"><div class="tit">Dirección</div>
          <div class="val">${v.direccion||"—"}</div>
          <div class="val">${v.comuna||""} — ${v.ciudad||""}</div></div>
        <div class="sec"><div class="tit">Teléfono</div>
          <div class="val">${v.telefono||"—"}</div></div>
        <div class="sec"><div class="tit">Email</div>
          <div class="val">${v.email||"—"}</div></div>
        <div class="sec"><div class="tit">Total</div>
          <div class="val">$${formatPrice(v.total)} + IVA</div></div>
      </div>
      <br/><button onclick="window.print()">🖨️ Imprimir</button>
    </body></html>`);
    w.document.close();
  };

  const ventasFiltradas = filtro === "TODOS"
    ? ventas
    : ventas.filter(v => v.estadoEnvio === filtro);

  if (loading) return <p style={{ textAlign: "center", marginTop: 40 }}>Cargando pedidos...</p>;
  if (error)   return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;

  return (
    <div style={st.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📦 Gestión de Pedidos</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Filtrar por estado:</label>
          <select value={filtro} onChange={e => setFiltro(e.target.value)} style={st.select}>
            <option value="TODOS">Todos</option>
            {ESTADOS_ENVIO.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <button onClick={cargar} style={st.btnRefresh}>↺ Actualizar</button>
        </div>
      </div>

      {ventasFiltradas.length === 0 ? (
        <p style={{ color: "#888" }}>No hay pedidos para mostrar.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={st.tabla}>
            <thead>
              <tr style={st.thead}>
                <th style={{ width: 32 }}></th>
                <th>#</th>
                <th>Cliente</th>
                <th>Dirección</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Envío</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ventasFiltradas.map(v => (
                <FilaPedido
                  key={v.id}
                  v={v}
                  onCambiarEstado={handleCambiarEstado}
                  onImprimir={imprimirEtiqueta}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────────
const st = {
  container:    { padding: "24px 16px" },
  tabla:        { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  thead:        { backgroundColor: "#1a1a2e", color: "#fff" },
  fila:         { borderBottom: "1px solid #e5e7eb", verticalAlign: "top" },
  tablaDetalle: { width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 },
  acciones:     { display: "flex", flexDirection: "column", gap: 6, minWidth: 170 },
  selectorGrupo:{ display: "flex", flexDirection: "column", gap: 4 },
  labelSelect:  { fontSize: 10, color: "#666", fontWeight: 700, textTransform: "uppercase" },
  select:       { padding: "5px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 12 },
  btnCambiar: {
    padding: "5px 10px", backgroundColor: "#16a34a", color: "#fff",
    border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600,
  },
  btnEtiqueta: {
    padding: "5px 10px", backgroundColor: "#374151", color: "#fff",
    border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12,
  },
  btnExpand: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 12, color: "#666", padding: "2px 4px",
  },
  btnRefresh: {
    padding: "6px 12px", backgroundColor: "#f1f5f9", color: "#374151",
    border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer", fontSize: 13,
  },
};

// Estilos de celdas via hoja de estilos global
const styleEl = document.createElement("style");
styleEl.textContent = `
  .admin-ventas-tabla td, .admin-ventas-tabla th { padding: 8px 10px; text-align: left; }
  .admin-ventas-tabla tr:hover td { background-color: #f8fafc; }
`;
document.head.appendChild(styleEl);

export default AdminVentas;
